/**
 * Source files button + dropdown — the original uploads (media plan, IO PDF,
 * launch template, …) recorded against a campaign in `campaign_files`.
 *
 * Platform-agnostic: every create path (planning, fast-track, orchestration)
 * writes campaign_files rows, so this works for DV360 / TTD / GT / Google Ads /
 * Facebook alike. Campaigns created before those rows existed simply show an
 * empty list.
 *
 * Each click re-signs through the per-file download route rather than reusing
 * the URL from the list, so a dropdown left open past the signed URL's expiry
 * still downloads.
 *
 * API:
 *   GET /api/v2/campaigns/:id                         → files[]
 *   GET /api/v2/campaigns/:id/files/:fileId/download  → { publicUrl }
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { api, getApiUrl } from '../../utils/apiConfig';

function fmtSize(bytes) {
  const n = Number(bytes) || 0;
  if (n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

export default function SourceFilesButton({ campaignId, accountId, userId }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const rootRef = useRef(null);

  // Close the dropdown when the user clicks anywhere outside it.
  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const refresh = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}`), {
        params: { accountId, userId },
      });
      setFiles(Array.isArray(res.data?.files) ? res.data.files : []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load source files');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [campaignId, accountId, userId]);

  const onToggle = () => {
    if (!open) refresh();
    setOpen((v) => !v);
  };

  const onDownload = async (file) => {
    setDownloading(file.id);
    setError(null);
    try {
      const res = await api.get(
        getApiUrl(`/api/v2/campaigns/${campaignId}/files/${file.id}/download`),
        { params: { accountId, userId } },
      );
      const url = res.data?.publicUrl;
      if (!url) throw new Error('No download URL returned');
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="clw-btn clw-btn-secondary clw-btn-sm"
        onClick={onToggle}
        disabled={!campaignId}
        title="Original uploaded files for this campaign (media plan, IO, template)"
      >
        Source files {files ? `(${files.length})` : '▾'}
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 1000,
          minWidth: 360, maxWidth: 520, maxHeight: 420, overflowY: 'auto',
          background: 'var(--bg-elevated, #fff)', border: '1px solid var(--g3, #d0d0d0)',
          borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: 8,
        }}>
          <div style={{ padding: '6px 8px', fontSize: 12, color: 'var(--g4)', borderBottom: '1px solid var(--g2, #eee)' }}>
            Uploaded files {loading ? '· loading…' : files ? `· ${files.length}` : ''}
          </div>
          {error && <div style={{ padding: 8, color: 'var(--red, #c00)', fontSize: 12 }}>{error}</div>}
          {!loading && files && files.length === 0 && (
            <div style={{ padding: 12, color: 'var(--g4)', fontSize: 12, textAlign: 'center' }}>
              No uploaded files are recorded for this campaign.
            </div>
          )}
          {files && files.map((f) => {
            const meta = [fmtSize(f.file_size), fmtDate(f.created_at)].filter(Boolean).join(' · ');
            return (
              <div
                key={f.id}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderBottom: '1px solid var(--g2, #eee)' }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.original_name}>
                    {f.original_name}
                  </div>
                  {meta && <div style={{ fontSize: 11, color: 'var(--g4)' }}>{meta}</div>}
                </div>
                <button
                  type="button"
                  className="clw-btn clw-btn-secondary clw-btn-sm"
                  onClick={() => onDownload(f)}
                  disabled={downloading === f.id}
                >
                  {downloading === f.id ? 'Preparing…' : '⬇ Download'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
